import React, { useState, useEffect } from 'react';
import { ordersAPI, walletAPI } from '../../services/api';
import { useCurrency } from '../../contexts/CurrencyContext';
import WithdrawalModal from './WithdrawalModal';
import {
  CurrencyDollarIcon,
  ArrowTrendingUpIcon,
  BanknotesIcon,
  ChartBarIcon,
  CalendarIcon,
  ClockIcon,
  ArrowDownTrayIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';

const Earnings: React.FC = () => {
  const { formatPrice } = useCurrency();
  const [earnings, setEarnings] = useState({
    today: 0,
    week: 0,
    month: 0,
    total: 0,
    availableBalance: 0,
    pendingBalance: 0
  });
  const [transactions, setTransactions] = useState<any[]>([]);
  const [withdrawals, setWithdrawals] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState<'today' | 'week' | 'month'>('today');
  const [isWithdrawalModalOpen, setIsWithdrawalModalOpen] = useState(false);

  useEffect(() => {
    fetchEarnings();
    fetchWithdrawals();
  }, []);

  const fetchEarnings = async () => {
    try {
      setLoading(true);
      
      // Fetch actual wallet data
      const walletData = await walletAPI.getWallet();
      const ordersData = await ordersAPI.getUserOrders();
      
      // Calculate earnings
      const completedOrders = (ordersData || []).filter((order: any) => 
        order.status === 'delivered'
      );
      
      const totalEarnings = completedOrders.reduce((sum: number, order: any) => 
        sum + Number(order.total_price ?? 0), 0
      );
      
      setEarnings({
        today: totalEarnings * 0.3,
        week: totalEarnings * 0.6,
        month: totalEarnings,
        total: totalEarnings,
        availableBalance: walletData?.available_balance || walletData?.balance || 0,
        pendingBalance: walletData?.pending_balance || 0
      });

      // Mock transactions
      setTransactions(completedOrders.slice(0, 10).map((order: any) => ({
        id: order.id,
        type: 'delivery',
        amount: Number(order.total_price ?? 0),
        date: order.created_at || new Date().toISOString(),
        description: `Delivery: ${order.pickup_location} → ${order.dropoff_location}`,
        status: 'completed'
      })));
    } catch (error) {
      console.error('Error fetching earnings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchWithdrawals = async () => {
    try {
      const data = await walletAPI.getWithdrawals();
      setWithdrawals(data || []);
    } catch (error) {
      console.error('Error fetching withdrawals:', error);
    }
  };

  const handleWithdrawalSuccess = () => {
    fetchWithdrawals();
    fetchEarnings();
  };

  const getPeriodEarnings = () => {
    switch (selectedPeriod) {
      case 'today':
        return earnings.today;
      case 'week':
        return earnings.week;
      case 'month':
        return earnings.month;
      default:
        return earnings.today;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-volexx-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Earnings & Wallet
          </h1>
          <p className="mt-2 text-gray-600">Track your earnings and manage your wallet</p>
        </div>

        {/* Wallet Balance Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Available Balance */}
          <div className="bg-gradient-blue rounded-volexx shadow-volexx p-8">
            <div className="flex items-center justify-between text-white">
              <div>
                <p className="text-sm opacity-90 mb-2">Available Balance</p>
                <p className="text-4xl font-bold">{formatPrice(earnings.availableBalance)}</p>
                <button 
                  onClick={() => setIsWithdrawalModalOpen(true)}
                  className="mt-4 bg-white text-volexx-blue-600 px-6 py-2 rounded-volexx font-semibold hover:bg-gray-100 transition-colors flex items-center gap-2"
                >
                  <ArrowDownTrayIcon className="h-5 w-5" />
                  Withdraw Funds
                </button>
              </div>
              <BanknotesIcon className="h-16 w-16 opacity-50" />
            </div>
          </div>

          {/* Pending Balance */}
          <div className="bg-gradient-orange rounded-volexx shadow-volexx p-8">
            <div className="flex items-center justify-between text-white">
              <div>
                <p className="text-sm opacity-90 mb-2">Pending Balance</p>
                <p className="text-4xl font-bold">{formatPrice(earnings.pendingBalance)}</p>
                <p className="mt-4 text-sm opacity-90">
                  Pending deliveries settlement
                </p>
              </div>
              <ClockIcon className="h-16 w-16 opacity-50" />
            </div>
          </div>
        </div>

        {/* Earnings Stats */}
        <div className="bg-white rounded-volexx shadow-volexx border border-gray-100 p-6 mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900">Earnings Overview</h2>
            <div className="flex space-x-2">
              <button
                onClick={() => setSelectedPeriod('today')}
                className={`px-4 py-2 rounded-volexx font-medium text-sm transition-colors ${
                  selectedPeriod === 'today'
                    ? 'bg-volexx-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Today
              </button>
              <button
                onClick={() => setSelectedPeriod('week')}
                className={`px-4 py-2 rounded-volexx font-medium text-sm transition-colors ${
                  selectedPeriod === 'week'
                    ? 'bg-volexx-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                This Week
              </button>
              <button
                onClick={() => setSelectedPeriod('month')}
                className={`px-4 py-2 rounded-volexx font-medium text-sm transition-colors ${
                  selectedPeriod === 'month'
                    ? 'bg-volexx-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                This Month
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <div className="text-center p-4 bg-gray-50 rounded-volexx">
              <p className="text-sm text-gray-600 mb-1">Earnings</p>
              <p className="text-3xl font-bold text-volexx-blue-600">
                {formatPrice(getPeriodEarnings())}
              </p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-volexx">
              <p className="text-sm text-gray-600 mb-1">Commission</p>
              <p className="text-3xl font-bold text-volexx-orange-600">
                {formatPrice(getPeriodEarnings() * 0.15)}
              </p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-volexx">
              <p className="text-sm text-gray-600 mb-1">Net Income</p>
              <p className="text-3xl font-bold text-green-600">
                {formatPrice(getPeriodEarnings() * 0.85)}
              </p>
            </div>
          </div>
        </div>

        {/* Total Lifetime Earnings */}
        <div className="bg-white rounded-volexx shadow-volexx border border-gray-100 p-6 mb-8">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Total Lifetime Earnings</p>
              <p className="text-4xl font-bold text-gray-900">{formatPrice(earnings.total)}</p>
            </div>
            <div className="bg-green-100 p-4 rounded-volexx">
              <ArrowTrendingUpIcon className="h-10 w-10 text-green-600" />
            </div>
          </div>
        </div>

        {/* Transaction History */}
        <div className="bg-white rounded-volexx shadow-volexx border border-gray-100">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900">Transaction History</h2>
          </div>
          <div className="divide-y divide-gray-100">
            {transactions.length === 0 ? (
              <div className="px-6 py-12 text-center">
                <ChartBarIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No transactions yet</p>
                <p className="text-sm text-gray-400 mt-1">Complete deliveries to start earning</p>
              </div>
            ) : (
              transactions.map((transaction) => (
                <div key={transaction.id} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-start space-x-3">
                      <div className="bg-green-100 p-2 rounded-volexx">
                        <CurrencyDollarIcon className="h-5 w-5 text-green-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{transaction.description}</p>
                        <div className="flex items-center space-x-2 mt-1">
                          <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded-volexx capitalize">
                            {transaction.status}
                          </span>
                          <p className="text-xs text-gray-500">
                            <CalendarIcon className="h-3 w-3 inline mr-1" />
                            {new Date(transaction.date).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </div>
                    <p className="text-lg font-bold text-green-600">
                      +{formatPrice(transaction.amount)}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Withdrawal Requests */}
        {withdrawals.length > 0 && (
          <div className="mt-8 bg-white rounded-volexx shadow-volexx border border-gray-100">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">Withdrawal Requests</h2>
            </div>
            <div className="divide-y divide-gray-100">
              {withdrawals.map((withdrawal) => (
                <div key={withdrawal.id} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-start space-x-3">
                      <div className={`p-2 rounded-volexx ${
                        withdrawal.status === 'completed' ? 'bg-green-100' :
                        withdrawal.status === 'failed' || withdrawal.status === 'cancelled' ? 'bg-red-100' :
                        'bg-amber-100'
                      }`}>
                        <ArrowDownTrayIcon className={`h-5 w-5 ${
                          withdrawal.status === 'completed' ? 'text-green-600' :
                          withdrawal.status === 'failed' || withdrawal.status === 'cancelled' ? 'text-red-600' :
                          'text-amber-600'
                        }`} />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          Withdrawal to {withdrawal.bank_name}
                        </p>
                        <p className="text-xs text-gray-500">
                          Account: {withdrawal.account_number}
                        </p>
                        <div className="flex items-center space-x-2 mt-1">
                          <span className={`px-2 py-1 text-xs font-semibold rounded-volexx capitalize ${
                            withdrawal.status === 'completed' ? 'bg-green-100 text-green-800' :
                            withdrawal.status === 'failed' || withdrawal.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                            withdrawal.status === 'processing' ? 'bg-blue-100 text-blue-800' :
                            'bg-amber-100 text-amber-800'
                          }`}>
                            {withdrawal.status}
                          </span>
                          <p className="text-xs text-gray-500">
                            <CalendarIcon className="h-3 w-3 inline mr-1" />
                            {new Date(withdrawal.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        {withdrawal.rejection_reason && (
                          <p className="text-xs text-red-600 mt-1">
                            Reason: {withdrawal.rejection_reason}
                          </p>
                        )}
                      </div>
                    </div>
                    <p className="text-lg font-bold text-gray-900">
                      -{formatPrice(withdrawal.amount)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Withdrawal Info */}
        <div className="mt-6 bg-volexx-blue-50 border border-volexx-blue-100 rounded-volexx p-4">
          <p className="text-sm text-volexx-blue-800">
            <strong>Note:</strong> Withdrawals are processed within 1-3 business days. 
            Minimum withdrawal amount is ₦10,000. Commission rate is 15% per delivery.
          </p>
        </div>
      </div>

      {/* Withdrawal Modal */}
      <WithdrawalModal
        isOpen={isWithdrawalModalOpen}
        onClose={() => setIsWithdrawalModalOpen(false)}
        availableBalance={earnings.availableBalance}
        onSuccess={handleWithdrawalSuccess}
      />
    </div>
  );
};

export default Earnings;
